import apiClient, { setAuthToken } from '@/lib/api/apiClient'
import { parseVerifyUserResponse } from '@/lib/api/userService'
import { setAuthFromToken } from '@/features/auth/authSlice'
import { setStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'
import type { AppDispatch } from '@/app/store'
import { ROUTE_BUILDERS, ROUTE_PATHS } from '@/config/routes'
import { clearImpersonationSessionFilters } from '@/lib/auth/impersonationSessionFilters'
import { normalizeImpersonateReturnPath } from '@/lib/utils/impersonateReturnPath'

/**
 * Return path saved when impersonation started. Angular uses `impersonatepath` only; React also sets `impersonate_path`.
 */
export function peekImpersonateReturnPath(): string | null {
  try {
    return (
      localStorage.getItem('impersonatepath') ||
      localStorage.getItem('impersonate_path') ||
      null
    )
  } catch {
    return null
  }
}

function clearImpersonationFlags(): void {
  try {
    localStorage.removeItem('impersonate')
    localStorage.removeItem('impersonatepath')
    localStorage.removeItem('impersonate_path')
    sessionStorage.removeItem('impersonate_process')
    sessionStorage.removeItem('impersonateprocess')
  } catch {
    /* ignore */
  }
}

export interface EndAdminImpersonationOptions {
  dispatch: AppDispatch
  /**
   * When the stored return path is empty or generic `/dashboard`, use applicant admin targets
   * (matches React ApplicantDashboard stop behavior).
   */
  applicantFallback?: {
    originalApplicantId: string | null
    applicationsListPath?: string
  }
}

/**
 * End Liberty admin → user impersonation: DELETE `/api/impersonate`, restore the
 * prior token, clear list filters, GET `/api/verify?force`, update user in store.
 *
 * @returns URL to navigate to (caller usually sets `window.location.href`).
 */
export async function endAdminImpersonation(
  options: EndAdminImpersonationOptions,
): Promise<string> {
  const storedReturn = peekImpersonateReturnPath()
  const { dispatch, applicantFallback } = options

  const adminToken =
    (typeof localStorage !== 'undefined' && localStorage.getItem('admin_token')) ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('old_token')) ||
    null

  if (!adminToken) {
    clearImpersonationFlags()
    clearImpersonationSessionFilters()
    try {
      localStorage.removeItem('impersonated_applicant_id')
    } catch {
      /* ignore */
    }
    return ROUTE_PATHS.LOGIN
  }

  try {
    await apiClient.delete('/api/impersonate')
  } catch {
    /* Angular still proceeds on failure in some edge cases; continue to restore admin session */
  }

  clearImpersonationFlags()
  clearImpersonationSessionFilters()

  try {
    localStorage.removeItem('impersonated_applicant_id')
  } catch {
    /* ignore */
  }

  setAuthToken(adminToken)

  localStorage.removeItem('admin_token')
  localStorage.removeItem('old_token')
  setStorageItem(STORAGE_KEYS.authToken, adminToken)

  const verifyRes = await apiClient.get('/api/verify?force')
  const userData = parseVerifyUserResponse(verifyRes.data)
  dispatch(setAuthFromToken({ token: adminToken, user: userData }))
  try {
    localStorage.setItem('user', JSON.stringify(userData))
  } catch {
    /* ignore */
  }

  let redirect = normalizeImpersonateReturnPath(storedReturn)
  if (applicantFallback) {
    const listPath =
      applicantFallback.applicationsListPath ?? ROUTE_PATHS.DASHBOARD_APPLICATIONS
    if (!storedReturn || redirect === ROUTE_PATHS.DASHBOARD) {
      if (applicantFallback.originalApplicantId) {
        redirect = ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(
          applicantFallback.originalApplicantId,
        )
      } else {
        redirect = listPath
      }
    }
  }

  return redirect
}
